import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { activeNetworkSelector } from 'redux/selectors';
import { Box } from 'rebass/styled-components';
import toast from 'react-hot-toast';
import NoAccess from 'components/atoms/NoAccess';
import { useGetGeneralSettingsQuery, useUpdateGeneralSettingsMutation } from 'services/network';
import strings from 'strings';
import CrmContactsSection from '../components/CrmContacts';
import { SettingsLayoutHeader } from '../../../components/atoms/settingsLayoutHeader';
import usePageHook from '../../../hooks/usePageHook';

const pageTitle = 'Contacts';

const CrmContacts = () => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const { data: generalSettings, isLoading, isError: fetchError } = useGetGeneralSettingsQuery();
  const [updateSettings] = useUpdateGeneralSettingsMutation();
  const [optimisticValue, setOptimisticValue] = useState(null);

  usePageHook(pageTitle);

  const currentValue = optimisticValue !== null ? optimisticValue : Boolean(generalSettings?.sortUnenrichedToBottom);

  const handleToggle = useCallback(async () => {
    const newValue = !currentValue;
    setOptimisticValue(newValue);

    const result = await updateSettings({ sortUnenrichedToBottom: newValue });

    if (result.error) {
      setOptimisticValue(null);
      toast(strings.genericError, { icon: 'error', id: 'crm-contacts-settings' });
      return;
    }

    toast('Changes saved successfully', { icon: 'success', id: 'crm-contacts-settings' });
  }, [currentValue, updateSettings]);

  if (!activeNetwork.isManager) {
    return <NoAccess cy="settings-unavailable" />;
  }

  return (
    <Box maxWidth="600px">
      <SettingsLayoutHeader
        title={pageTitle}
        description="Control how contacts are displayed and sorted across your network."
      />
      <Box mt="32px">
        <CrmContactsSection
          sortUnenrichedToBottom={currentValue}
          isLoading={isLoading}
          fetchError={Boolean(fetchError)}
          onToggle={handleToggle}
        />
      </Box>
    </Box>
  );
};

export default CrmContacts;
